<template>
  <div>
    <!-- Loading skeleton (form fields) -->
    <div v-if="loading">
      <b-card>
        <b-skeleton width="40%" height="2rem" class="mb-2"/>
        <b-skeleton width="70%" class="mb-1"/>
        <b-skeleton width="55%" class="mb-1"/>
        <b-skeleton width="40%" height="2rem" class="mb-2 mt-3"/>
        <b-skeleton width="70%" class="mb-1"/>
        <b-skeleton width="55%" class="mb-1"/>
        <b-skeleton width="40%" height="2rem" class="mb-2 mt-3"/>
        <b-skeleton width="70%" class="mb-1"/>
        <b-skeleton width="55%" class="mb-1"/>
      </b-card>
    </div>

    <b-card v-else title="Alert Preferences">
      <b-row>
        <b-col md="6">
          <b-form-group label="WhatsApp Number">
            <b-form-input v-model="form.whatsapp_number" placeholder="+91XXXXXXXXXX" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="SMS Number">
            <b-form-input v-model="form.sms_number" placeholder="+91XXXXXXXXXX" />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <b-form-group :label="`Mortality Threshold (%) — Current: ${form.mortality_threshold}%`">
            <b-form-input
              v-model="form.mortality_threshold"
              type="range"
              min="0"
              max="20"
              step="0.5"
            />
            <small class="text-muted">Alert when mortality exceeds this %</small>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group :label="`Feed Drop Threshold (%) — Current: ${form.feed_drop_threshold}%`">
            <b-form-input
              v-model="form.feed_drop_threshold"
              type="range"
              min="0"
              max="50"
              step="1"
            />
            <small class="text-muted">Alert when feed consumption drops by this %</small>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <b-form-group label="Temperature High Threshold (°C)">
            <b-form-input v-model="form.temp_high_threshold" type="number" step="0.5" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Temperature Low Threshold (°C)">
            <b-form-input v-model="form.temp_low_threshold" type="number" step="0.5" />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <b-form-group label="Low Stock Alert Days">
            <b-form-input v-model="form.low_stock_days_threshold" type="number" min="1" />
            <small class="text-muted">Alert when stock will last less than this many days</small>
          </b-form-group>
        </b-col>
        <b-col md="6" class="d-flex align-items-center mt-2">
          <b-form-group label="Alerts Enabled">
            <b-form-checkbox v-model="form.alerts_enabled" switch>
              {{ form.alerts_enabled ? 'Enabled' : 'Disabled' }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <b-alert v-if="Object.keys(errors).length" variant="danger" show class="mt-2">
        <div v-for="(msg, key) in errors" :key="key">{{ msg }}</div>
      </b-alert>

      <b-row class="mt-2">
        <b-col>
          <b-button variant="primary" :disabled="saving" @click="saveSettings">
            <b-spinner v-if="saving" small class="mr-1" />
            Save Settings
          </b-button>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import {
  BCard, BRow, BCol, BButton, BSpinner, BAlert,
  BFormGroup, BFormInput, BFormCheckbox,
} from 'bootstrap-vue'

export default {
  name: 'NotificationSettings',
  components: {
    BCard, BRow, BCol, BButton, BSpinner, BAlert,
    BFormGroup, BFormInput, BFormCheckbox,
  },
  data() {
    return {
      loading: false,
      saving: false,
      errors: {},
      form: {
        whatsapp_number: '',
        sms_number: '',
        mortality_threshold: 3.0,
        feed_drop_threshold: 20.0,
        temp_high_threshold: 35,
        temp_low_threshold: 18,
        low_stock_days_threshold: 5,
        alerts_enabled: true,
      },
    }
  },
  created() {
    this.fetchSettings()
  },
  methods: {
    async fetchSettings() {
      this.loading = true
      try {
        const { data } = await axios.get('web/notification-settings')
        this.form = { ...this.form, ...(data?.data || {}) }
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to load settings', { variant: 'danger', title: 'Error' })
      } finally {
        this.loading = false
      }
    },
    validateSettings() {
      this.errors = {}
      const mort = parseFloat(this.form.mortality_threshold)
      if (isNaN(mort) || mort < 0.1 || mort > 20) {
        this.errors.mortality_threshold = 'Mortality threshold must be between 0.1 and 20'
      }
      const tempHigh = parseFloat(this.form.temp_high_threshold)
      const tempLow = parseFloat(this.form.temp_low_threshold)
      if (!isNaN(tempHigh) && !isNaN(tempLow) && tempHigh <= tempLow) {
        this.errors.temp_threshold = 'Temperature high threshold must be greater than low threshold'
      }
      return Object.keys(this.errors).length === 0
    },
    async saveSettings() {
      if (!this.validateSettings()) return
      this.saving = true
      try {
        await axios.put('web/notification-settings', this.form)
        this.$bvToast.toast('Settings saved successfully', { variant: 'success', title: 'Saved' })
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to save settings', { variant: 'danger', title: 'Error' })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped></style>
